import {
  DataGrid,
  GridActionsCell,
  type GridColDef,
  type GridColumnVisibilityModel,
  GridActionsCellItem,
  type GridRowModesModel,
  type GridRowId,
  GridRowModes,
  type GridEventListener,
  GridRowEditStopReasons,
} from '@mui/x-data-grid'
import {
  Box,
  TextField,
  Typography,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import SearchField from 'components/search-field'
import { t } from 'i18next'
import { useEffect, useRef, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchResellers,
  editReseller,
  fetchEligibleTenants,
  createReseller,
} from 'api/reseller'
import type { AppState } from 'state/index'
import { get } from 'es-toolkit/compat'
import { trackEvent } from 'api/usage-tracking'
import { TEST_IDS } from 'test-ids'

type ResellerData = {
  id: string
  name: string
  email: string
  discountRate: number
  accountEnabled: boolean
}

type ResellerApiItem = {
  id: string
  name: string
  email: string
  discountRate?: number
  accountEnabled: boolean
}

export default function Reseller() {
  const dispatch = useDispatch()
  const permissions = useSelector(
    (state: AppState) => state.account.permissions,
  )
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const [rowCount, setRowCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [rowSelected, setSelectedRow] = useState<string | number>(-1)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const [resellerList, setResellerList] = useState<ResellerData[]>([])

  const [columns, setColumns] = useState<GridColDef[]>([])
  const isAdmin = permissions.includes('farmtrx:admin')
  const [touchedFields, setTouchedFields] = useState<
    Record<string, Set<string>>
  >({})

  // Create Reseller Dialog State
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<any>(null)
  const [tenantOptions, setTenantOptions] = useState<any[]>([])
  const [tenantSearchInput, setTenantSearchInput] = useState<string>('')
  const [tenantSearchLoading, setTenantSearchLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  })
  const tenantSearchTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined)

  function fetchData(
    page: number,
    pageSize: number,
    search: string,
    ignore: boolean,
  ) {
    fetchResellers(page, pageSize, search)
      .then((res) => {
        if (!ignore) {
          const {
            results,
            pagination,
          }: { results?: ResellerApiItem[]; pagination?: { total?: number } } =
            res.data
          const _resellerList = (results || []).map(
            (reseller: ResellerApiItem) => ({
              id: reseller.id,
              name: reseller.name,
              email: reseller.email,
              discountRate: reseller.discountRate || 0,
              accountEnabled: reseller.accountEnabled,
            }),
          )
          setResellerList(_resellerList)
          setRowCount(pagination?.total || 0)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    let ignore = false
    setLoading(true)
    fetchData(
      (paginationModel.page || 0) + 1,
      paginationModel.pageSize,
      debouncedSearch,
      ignore,
    )
    return () => {
      ignore = true
    }
  }, [paginationModel, debouncedSearch])

  const onSearch = (value: string) => {
    setSearchValue(value)
  }

  const handleEdit = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.Edit },
      }))
    },
    [setRowModesModel],
  )

  const handleSave = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View },
      }))
    },
    [setRowModesModel],
  )

  const saveData = useCallback(
    async (dataToSave: any) => {
      const obj = {
        discountRate: Number(dataToSave.discountRate),
        accountEnabled: dataToSave.accountEnabled,
      }
      await editReseller(dataToSave.id, obj)
      trackEvent('reseller_edited', {
        discount: dataToSave.discountRate,
        accountEnabled: dataToSave.accountEnabled,
      })
      setSelectedRow(-1)
      return dataToSave
    },
    [rowSelected],
  )

  const processRowUpdate = async (
    newRow: ResellerData,
    oldRow: ResellerData,
  ) => {
    const result = await saveData(newRow)
    setResellerList((prevRows) =>
      prevRows.map((row) => (row.id === result.id ? result : row)),
    )
    return result
  }

  const handleCancel = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }))
      const originalRow = resellerList.find((row) => row.id === id)
      if (originalRow) {
        setResellerList((prevRows) =>
          prevRows.map((row) => (row.id === id ? originalRow : row)),
        )
      }
    },
    [setRowModesModel, resellerList],
  )

  // Handle tenant search in Autocomplete
  const handleTenantSearchChange = useCallback((inputValue: string) => {
    setTenantSearchInput(inputValue)

    if (tenantSearchTimeoutRef.current) {
      clearTimeout(tenantSearchTimeoutRef.current)
    }

    if (!inputValue.trim()) {
      setTenantSearchLoading(false)
      setTenantOptions([])
      return
    }

    setTenantSearchLoading(true)
    tenantSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetchEligibleTenants(inputValue, 1, 50)
        setTenantOptions(response.data.results || [])
      } catch (error) {
        console.error('Failed to fetch eligible tenants:', error)
        setTenantOptions([])
      } finally {
        setTenantSearchLoading(false)
      }
    }, 300)
  }, [])

  // Handle create reseller button click
  const handleOpenCreateDialog = useCallback(() => {
    setCreateDialogOpen(true)
    setSelectedTenant(null)
    setTenantSearchInput('')
    setTenantOptions([])
  }, [])

  // Handle close dialog
  const handleCloseCreateDialog = useCallback(() => {
    setCreateDialogOpen(false)
    setSelectedTenant(null)
    setTenantSearchInput('')
    setTenantOptions([])
  }, [])

  // Handle add reseller
  const handleAddReseller = useCallback(async () => {
    if (!selectedTenant) return

    setCreating(true)
    try {
      await createReseller(selectedTenant.id)
      trackEvent('reseller_created', { tenantId: selectedTenant.id })

      // Refresh the reseller list
      setResellerList([])
      setRowCount(0)
      setPaginationModel({ page: 0, pageSize: 10 })

      // Show success message
      setSnackbar({
        open: true,
        message: t('reseller_created'),
        severity: 'success',
      })

      // Close dialog
      handleCloseCreateDialog()
    } catch (error: any) {
      console.error('Failed to create reseller:', error)
      setSnackbar({
        open: true,
        message: error?.response?.data?.error || t('error_creating_reseller'),
        severity: 'error',
      })
    } finally {
      setCreating(false)
    }
  }, [selectedTenant, handleCloseCreateDialog])

  // Handle close snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false })
  }, [snackbar])

  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 300)
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    }
  }, [searchValue])

  const markTouched = useCallback((rowId: string, field: string) => {
    setTouchedFields((prev) => {
      const existing = prev[rowId] ? new Set(prev[rowId]) : new Set<string>()
      existing.add(field)
      return { ...prev, [rowId]: existing }
    })
  }, [])

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const [columnVisibility, setColumnVisibility] =
    useState<GridColumnVisibilityModel>({
      name: true,
      email: true,
      discountRate: true,
      accountEnabled: true,
    })

  useEffect(() => {
    const _columns: GridColDef[] = [
      {
        field: 'name',
        headerName: t('name'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
      },
      {
        field: 'email',
        headerName: t('email'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
      },
      {
        field: 'discountRate',
        headerName: t('discount_rate'),
        minWidth: 150,
        maxWidth: 200,
        align: 'left',
        flex: 1,
        editable: true,
        renderCell: (params) => `${params.row.discountRate}%`,
        preProcessEditCellProps: (params) => {
          let error = false
          const parsedValue = Number(params.props.value)
          if (
            !Number.isFinite(parsedValue) ||
            parsedValue < 0 ||
            parsedValue > 100
          ) {
            error = true
          }

          return { ...params.props, error }
        },
        renderEditCell: (params) => {
          const parsedValue = Number(params.row.discountRate)
          const isTouched =
            !!touchedFields[params.id as string]?.has('discountRate')
          const hasError =
            isTouched &&
            (!Number.isFinite(parsedValue) ||
              parsedValue < 0 ||
              parsedValue > 100)
          return (
            <TextField
              data-testid={TEST_IDS.resellerDiscountInput}
              value={params.row.discountRate ?? ''}
              onChange={(e) => {
                const value = e.target.value
                setResellerList((prevRows: ResellerData[]) =>
                  prevRows.map((row) =>
                    row.id === params.id
                      ? { ...row, discountRate: Number(value) }
                      : row,
                  ),
                )
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'discountRate',
                  value,
                })
              }}
              onBlur={() => markTouched(params.id as string, 'discountRate')}
              error={hasError}
              label={t('enter_discount_rate')}
              size="small"
              fullWidth
              autoFocus={params.hasFocus}
              variant={'filled'}
              type="number"
            />
          )
        },
      },
      {
        field: 'accountEnabled',
        headerName: t('enabled'),
        minWidth: 150,
        maxWidth: 200,
        align: 'left',
        flex: 1,
        editable: true,
        preProcessEditCellProps: (params) => {
          return { ...params.props, error: false }
        },
        renderCell: (params) => (
          <Switch checked={params.row.accountEnabled} disabled />
        ),
        renderEditCell: (params) => (
          <Switch
            data-testid={TEST_IDS.resellerEnabledToggle}
            checked={params.row.accountEnabled}
            onChange={(e) => {
              const value = e.target.checked
              setResellerList((prevRows: ResellerData[]) =>
                prevRows.map((row) =>
                  row.id === params.id
                    ? { ...row, accountEnabled: value }
                    : row,
                ),
              )
              params.api.setEditCellValue({
                id: params.id,
                field: 'accountEnabled',
                value,
              })
            }}
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        editable: true,
        preProcessEditCellProps: (params) => {
          return { ...params.props, error: false }
        },
        renderCell: (params) => {
          return (
            <GridActionsCell {...params}>
              <GridActionsCellItem
                icon={<EditIcon />}
                label={t('edit')}
                onClick={() => handleEdit(params.id)}
              />
            </GridActionsCell>
          )
        },
        renderEditCell: (params) => {
          const { error, isProcessingProps, ...rest } = params
          return (
            <GridActionsCell {...rest}>
              <GridActionsCellItem
                icon={<SaveIcon />}
                label={t('save')}
                onClick={() => handleSave(rest.id)}
              />
              <GridActionsCellItem
                icon={<CancelIcon />}
                label={t('cancel')}
                onClick={() => handleCancel(rest.id)}
              />
            </GridActionsCell>
          )
        },
      },
    ]
    setColumns(_columns)
  }, [touchedFields])

  if (!isAdmin) {
    return <div>{t('no_permissions', { item: t('resellers') })}</div>
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 2,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <SearchField
          placeholder={t('search')}
          onSearch={onSearch}
          name={searchValue}
          className="reseller-search"
          sx={{ flex: 1 }}
        />
        <Button
          data-testid={TEST_IDS.createResellerButton}
          variant="contained"
          color="primary"
          onClick={handleOpenCreateDialog}
        >
          {t('create_reseller')}
        </Button>
      </Box>
      {resellerList.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>{t('no_resellers')}</Box>
      )}
      <DataGrid
        rows={resellerList}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20, 50]}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibility(newModel)
        }
        paginationMode="server"
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.error(error)
        }}
        sx={{
          flex: 1,
          '& .MuiDataGrid-root': {
            border: 'none',
          },
        }}
      />

      {/* Create Reseller Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
        data-testid={TEST_IDS.createResellerDialog}
      >
        <DialogTitle>{t('create_reseller')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: 1 }}>
            <Typography
              component="label"
              htmlFor="create-reseller-tenant-select"
              sx={{ minWidth: 80 }}
            >
              {t('tenant')}
            </Typography>
            <Autocomplete
              id="create-reseller-tenant-select"
              data-testid={TEST_IDS.createResellerTenantSelect}
              fullWidth
              style={{ width: '100%' }}
              sx={{ flex: 1, minWidth: 0, width: 1 }}
              options={tenantOptions}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <div>{option.name}</div>
                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                      {option.email} • {option.id}
                    </div>
                  </Box>
                </Box>
              )}
              loading={tenantSearchLoading}
              onInputChange={(event, value) => handleTenantSearchChange(value)}
              value={selectedTenant}
              onChange={(event, newValue) => setSelectedTenant(newValue)}
              inputValue={tenantSearchInput}
              filterOptions={(x) => x} // Server-side filtering
              noOptionsText={
                tenantSearchLoading ? t('loading') : t('no_results_found')
              }
              freeSolo={false}
              selectOnFocus
              clearOnBlur={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  style={{ width: '100%' }}
                  placeholder={t('select_tenant_placeholder')}
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {tenantSearchLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.slotProps.input.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={creating}>
            {t('cancel')}
          </Button>
          <Button
            data-testid={TEST_IDS.createResellerAddButton}
            onClick={handleAddReseller}
            variant="contained"
            color="primary"
            disabled={!selectedTenant || creating}
          >
            {creating ? <CircularProgress size={20} /> : t('add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
